import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import { formatPublished } from '@/lib/format';
import type { GsStory } from '@/lib/types';

function meta(s: GsStory) {
  return [s.author, formatPublished(s.published_at)].filter(Boolean).join(' \u00b7 ');
}

export default function LatestStories() {
  const { data: stories, loading, error, reload } = useTable<GsStory>('gs_stories');
  const lead = useMemo(() => stories.find((s) => s.is_lead) || stories[0], [stories]);
  const secondary = useMemo(
    () => stories
      .filter((s) => s.id !== lead?.id)
      .sort((a, b) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime())
      .slice(0, 5),
    [stories, lead],
  );

  return (
    <section id="news" className="bg-background-100 px-4 md:px-8 py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">News</p>
          <h2 className="font-heading font-bold text-4xl md:text-6xl text-foreground-950 leading-[1.02] mt-3">Latest Stories</h2>
        </div>
        <Link to="/news" className="inline-flex items-center gap-2 font-label uppercase tracking-widest text-xs font-semibold text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap">
          All stories<span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
        </Link>
      </div>

      {loading && <LoadingBlock label="Loading stories" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {lead && (
            <Link to={lead.slug ? `/news/${lead.slug}` : '/news'} className="group lg:col-span-7 relative block h-[420px] md:h-[620px] rounded-lg overflow-hidden cursor-pointer">
              <img src={lead.image} alt={lead.title} title={lead.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/35 to-transparent"></div>
              <div className="absolute bottom-0 p-5 md:p-8 w-full">
                <span className="inline-block bg-accent-500 text-foreground-950 font-label font-bold uppercase text-[11px] tracking-[0.18em] px-2.5 py-1 rounded-sm">
                  {lead.category}{lead.city ? ` \u00b7 ${lead.city}` : ''}
                </span>
                <h3 className="mt-4 font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-background-50 leading-[1.05] max-w-2xl">{lead.title}</h3>
                <p className="mt-3 font-label uppercase tracking-widest text-[11px] text-background-200">{meta(lead) || 'Featured story'}</p>
              </div>
            </Link>
          )}

          <div className="lg:col-span-5 flex flex-col">
            {secondary.map((s, i) => (
              <Link
                key={s.id}
                to={s.slug ? `/news/${s.slug}` : '/news'}
                className={`group flex gap-4 md:gap-5 py-4 md:py-5 cursor-pointer items-center ${i !== 0 ? 'border-t border-background-300' : ''}`}
              >
                <div className="w-24 h-20 md:w-32 md:h-24 shrink-0 overflow-hidden rounded-md">
                  <img src={s.image} alt={s.title} title={s.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="min-w-0">
                  <p className="font-label uppercase text-[11px] tracking-[0.18em] font-semibold text-primary-600">{s.category}</p>
                  <h3 className="mt-1.5 font-heading font-bold text-lg md:text-xl leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{s.title}</h3>
                  {meta(s) && <p className="mt-1.5 font-label uppercase tracking-widest text-[10px] text-foreground-500">{meta(s)}</p>}
                </div>
              </Link>
            ))}
            {secondary.length === 0 && <p className="text-foreground-600">More stories coming soon.</p>}
          </div>
        </div>
      )}
    </section>
  );
}